import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #f8fafc 0%, #e0e7ff 100%)',
  },
  card: {
    background: '#fff',
    padding: '2.5rem 2rem',
    borderRadius: '1.25rem',
    boxShadow: '0 8px 32px rgba(60,72,100,0.12)',
    minWidth: 340,
    maxWidth: 360,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '0.5rem',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '0.9rem',
    color: '#6b7280',
    marginBottom: '1.5rem',
    textAlign: 'center',
    lineHeight: 1.5,
  },
  email: {
    fontWeight: 600,
    color: '#374151',
  },
  codeContainer: {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'center',
    margin: '1rem 0',
  },
  codeInput: {
    width: '45px',
    height: '55px',
    textAlign: 'center',
    fontSize: '1.5rem',
    fontWeight: 600,
    borderRadius: '0.5rem',
    border: '1px solid #e5e7eb',
    outline: 'none',
    transition: 'border 0.2s',
  },
  button: {
    width: '100%',
    padding: '0.75rem',
    margin: '1rem 0 0.5rem 0',
    borderRadius: '0.5rem',
    border: 'none',
    background: '#000000',
    color: '#fff',
    fontWeight: 600,
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  buttonDisabled: {
    background: '#9ca3af',
    cursor: 'not-allowed',
  },
  resendContainer: {
    marginTop: '1rem',
    fontSize: '0.9rem',
    color: '#6b7280',
  },
  resendLink: {
    color: '#000000',
    fontWeight: 500,
    cursor: 'pointer',
    textDecoration: 'underline',
    background: 'none',
    border: 'none',
    fontSize: '0.9rem',
  },
  backLink: {
    color: '#6b7280',
    textDecoration: 'none',
    fontSize: '0.9rem',
    marginTop: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  error: {
    color: '#ef4444',
    fontSize: '0.95rem',
    marginTop: 4,
    textAlign: 'center',
  },
  success: {
    color: '#10b981',
    fontSize: '0.95rem',
    marginTop: 4,
    textAlign: 'center',
  },
};

export default function VerifyCode() {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email;

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    if (!email) {
      navigate('/forgot-password');
    }
  }, [email, navigate]);

  const handleChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Move to previous input on backspace if current is empty
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newCode = [...code];
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newCode[i] = pastedData[i];
      }
      setCode(newCode);
      // Focus the next empty input or the last one
      const nextEmptyIndex = newCode.findIndex((digit) => !digit);
      inputRefs.current[nextEmptyIndex === -1 ? 5 : nextEmptyIndex]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const fullCode = code.join('');
    if (fullCode.length !== 6) {
      setError('Please enter the complete 6-digit code.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/event-invites/auth/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: fullCode }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Invalid code. Please try again.');
        return;
      }

      // Success - navigate to reset password with the token
      navigate('/reset-password', { state: { email, resetToken: data.resetToken } });
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setSuccess('');

    try {
      await fetch(`${API_URL}/api/event-invites/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      setSuccess('A new code has been sent to your email.');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    }
  };

  const isCodeComplete = code.every((digit) => digit !== '');

  if (!email) return null;

  return (
    <div style={styles.container}>
      <form style={styles.card} onSubmit={handleSubmit}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
          <img
            src={require('../assets/top_logo.png')}
            alt="Invite Avenue Logo"
            style={{ width: '15vw', height: '20vh', objectFit: 'contain' }}
          />
        </div>
        <div style={styles.title}>Enter verification code</div>
        <div style={styles.subtitle}>
          We sent a 6-digit code to <span style={styles.email}>{email}</span>
        </div>
        <div style={styles.codeContainer}>
          {code.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              style={styles.codeInput}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              autoFocus={index === 0}
            />
          ))}
        </div>
        {error && <div style={styles.error}>{error}</div>}
        {success && <div style={styles.success}>{success}</div>}
        <button
          style={{ ...styles.button, ...((!isCodeComplete || loading) ? styles.buttonDisabled : {}) }}
          type="submit"
          disabled={!isCodeComplete || loading}
        >
          {loading ? 'Verifying...' : 'Verify code'}
        </button>
        <div style={styles.resendContainer}>
          Didn't receive the code?{' '}
          <button type="button" style={styles.resendLink} onClick={handleResend}>
            Resend
          </button>
        </div>
        <Link style={styles.backLink} to="/forgot-password" state={{ email }}>
          ← Change email
        </Link>
      </form>
    </div>
  );
}
